// Tali's Nailz — Main Theme JS
(function() {
  'use strict';

  // ── Toast ─────────────────────────────────────────────────────────
  function showToast(msg, duration) {
    const el = document.getElementById('toast');
    if (!el) return;
    el.textContent = msg;
    el.classList.add('is-visible');
    clearTimeout(el._timeout);
    el._timeout = setTimeout(() => el.classList.remove('is-visible'), duration || 2800);
  }

  /**
   * Turn a /cart/add.js failure into something a person can read.
   *
   * `description` is a plain string for ordinary failures ("sold out"), but for
   * gift card recipient fields Shopify returns a per-field object instead, e.g.
   *   { "recipient_email": ["is invalid"] }
   * Passing that straight into new Error() stringifies it to "[object Object]",
   * which is what the shopper was being shown when a gift card field was wrong.
   */
  function cartErrorMessage(err) {
    if (!err) return 'Could not add to cart';
    if (typeof err.description === 'string' && err.description) return err.description;

    const fields = (err.description && typeof err.description === 'object')
      ? err.description
      : err.errors;

    if (fields && typeof fields === 'object') {
      const parts = Object.keys(fields).map(function (key) {
        const value = fields[key];
        const text = Array.isArray(value) ? value.join(', ') : String(value);
        const label = key.replace(/^recipient_/, '').replace(/_/g, ' ');
        return (label.charAt(0).toUpperCase() + label.slice(1) + ' ' + text).trim();
      }).filter(Boolean);
      // Shopify sends the word lowercase ("is invalid"); capitalised reads as a
      // deliberate message rather than a raw field error leaking through.
      if (parts.length) return parts.join('. ').replace(/\binvalid\b/g, 'Invalid');
    }

    if (typeof err.message === 'string' && err.message) return err.message;
    return 'Could not add to cart';
  }

  // ── Cart ──────────────────────────────────────────────────────────
  // ── Production capacity ───────────────────────────────────────────
  // Capacity used to be the inventory count on a hidden $0 product,
  // `tn-availability`, that got dropped into every bag holding a nail set.
  // Shopify's own stock control made that an atomic hold, which is why it was
  // built that way. It was removed on 2026-09-06 because the line is a real
  // line item and behaved like one:
  //
  //   - customers saw "tn-availability  FREE" at checkout and on their receipt
  //   - nothing gave a hold back when a set was REMOVED from the bag, so
  //     browsing leaked capacity permanently
  //   - a stale hold rode along on a gift card order (#1001), took a slot, left
  //     the order stuck PARTIALLY_FULFILLED, and made the Worker file a gift
  //     card as a collection order and post "order received" into chat
  //
  // The count now lives in D1 and is spent by the orders/paid webhook, and it is
  // set by hand in headquarters.
  //
  // Since 2026-10-12 it no longer blocks adding to the bag at all. A full shop
  // blocks CHECKOUT, which main-cart.liquid does, and shows the banner below.
  // Everything here is read-only: one number, used to decide what to say.

  // null means "no trustworthy answer", never "none left". Every failure path
  // returns it, and the caller lets the sale through. A Worker hiccup closing
  // the whole shop is worse than occasionally taking one set too many.
  async function capacityRemaining() {
    const tn = window.TALISNAILZ || {};
    if (!tn.workerUrl) return null;
    try {
      const res = await fetch(tn.workerUrl + '/api/capacity', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}'
      });
      if (!res || !res.ok) return null;
      const data = await res.json();
      return data && data.known ? data.remaining : null;
    } catch (e) {
      return null;
    }
  }

  // Raise the sitewide banner when the shop is full.
  //
  // The banner is rendered hidden by layout/theme.liquid and revealed here,
  // because Liquid cannot read D1 and a cached page would lie about it anyway.
  // Fails closed on the banner: no answer means no banner, matching the rule
  // that a Worker hiccup must never make the shop look shut.
  async function initSoldOutBanner() {
    const el = document.getElementById('tn-sold-out-banner');
    if (!el || !window.TALISNAILZ?.enable_order_cap) return;
    const remaining = await capacityRemaining();
    if (remaining === 0) el.classList.remove('is-hidden');
  }

  // One-time cleanup for bags that still carry a hold from the old mechanism,
  // including saved bags restored from a customer's metafield. Without it those
  // lines stay invisible in the bag and surface again at checkout.
  async function purgeLegacyHolds() {
    try {
      const cart = await (await fetch('/cart.js')).json();
      const holds = cart.items.filter(i => (i.properties || {})['_hidden'] === 'true');
      if (!holds.length) return;
      for (const line of holds) await changeLine(line.key, 0).catch(() => {});
      await updateCartCount();
    } catch (e) {}
  }

  function changeLine(key, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    });
  }

  // Gift cards are the one thing anyone can buy: they need no nail profile and consume
  // no production capacity. Callers opt in with { giftCard: true }. Everything else is
  // made-to-order, so a signed-out shopper gets sent to the nail profile page instead.
  // The PDPs already swap their buttons server-side; this is the backstop.
  async function addToCart(variantId, quantity, properties, options) {
    const opts = options || {};

    if (!opts.giftCard && !window.TALISNAILZ?.customerId) {
      showToast('Sign in to add nail sets to your bag');
      setTimeout(function () { window.location.href = '/pages/nail-profile'; }, 1400);
      throw new Error('Sign in to add nail sets to your bag');
    }

    // Being at capacity no longer refuses the add (changed 2026-10-12, Tali's
    // call). You can fill a bag whenever you like; what a full shop blocks is
    // CHECKOUT, and the bag page says so. Turning a browsing shopper away at the
    // "add to bag" click lost the sale and the record of what they wanted;
    // letting the bag fill keeps both until a slot opens.
    //
    // The checkout block lives in main-cart.liquid. It is deliberately the only
    // gate, so there is one place to reason about rather than two that can
    // disagree.

    // Editing from the bag: remember what the line looked like before the
    // replacement goes in, so its quantity survives the swap.
    let oldLine = null;
    if (opts.replaceKey) {
      try {
        const before = await (await fetch('/cart.js')).json();
        oldLine = before.items.find(i => i.key === opts.replaceKey) || null;
      } catch (e) {}
    }

    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId), quantity: quantity || 1, properties: properties || {} }] })
    });

    if (!res.ok) {
      // Nothing to unwind. The capacity check above reserves nothing, so a
      // failed add leaves no trace to clean up.
      const err = await res.json().catch(() => null);
      throw new Error(cartErrorMessage(err));
    }

    const data = await res.json();

    // An edit replaces its line rather than adding a second one. The add runs
    // first on purpose: if it fails, the shopper still has the line they were
    // editing instead of an empty bag.
    if (opts.replaceKey) {
      const added = (data && data.items && data.items[0]) || null;
      const qty = oldLine ? oldLine.quantity : 1;
      // A cart line's key is derived from its variant and properties, so an
      // edit that changed nothing merges straight back into the line being
      // edited and bumps its quantity. Removing "the old key" there would
      // delete the item outright, so undo the bump instead.
      if (added && added.key === opts.replaceKey) {
        if (added.quantity !== qty) await changeLine(opts.replaceKey, qty).catch(() => {});
      } else {
        if (added && qty > 1) await changeLine(added.key, qty).catch(() => {});
        await changeLine(opts.replaceKey, 0).catch(() => {});
      }
    }

    await updateCartCount();
    await syncCartToServer();
    showToast(opts.replaceKey ? 'Your bag is updated ✦' : 'Added to your bag ✦');

    // Gift cards take no slot. An edit from the bag is already heading back to
    // the bag page, which says this better and without interrupting the edit.
    if (!opts.giftCard && !opts.replaceKey) await maybeCapacityPopup();

    return data;
  }

  async function updateCartCount() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      const countEl = document.getElementById('cart-count');
      if (countEl) {
        // Don't count hidden order slot items
        const visibleCount = cart.items.filter(i => !i.properties || !i.properties['_hidden']).reduce((sum, i) => sum + i.quantity, 0);
        countEl.textContent = visibleCount;
      }
    } catch(e) {}
  }

  async function removeFromCart(key) {
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 })
    });
    await updateCartCount();
    await syncCartToServer();
  }

  // ── Cross-device bag sync ─────────────────────────────────────────
  function propsKey(props) {
    const keys = Object.keys(props || {}).sort();
    return keys.map(k => k + '=' + props[k]).join('&');
  }

  async function syncCartToServer() {
    const tn = window.TALISNAILZ || {};
    if (!tn.customerId || !tn.workerUrl) return;
    try {
      const cart = await (await fetch('/cart.js')).json();
      const items = cart.items
        .filter(i => !i.properties || !i.properties['_hidden'])
        .map(i => ({ variantId: i.variant_id, quantity: i.quantity, properties: i.properties || {} }));
      // Worth one retry now that the reconcile treats an empty server bag as a
      // real answer and clears the device to match. A save that silently failed
      // used to just mean "no cross-device sync this time"; it would now read as
      // "this bag was emptied elsewhere" on the next load somewhere else.
      let saved = false;
      for (let attempt = 0; attempt < 2 && !saved; attempt++) {
        const res = await fetch(tn.workerUrl + '/api/cart/save', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ customerId: tn.customerId, ts: tn.authTs, sig: tn.authSig, items })
        }).catch(() => null);
        saved = !!(res && res.ok);
      }
      if (!saved) console.warn('[bag] could not save the bag to your account');
    } catch(e) {}
  }

  // A restored set still consumes production capacity, so it still owes a slot.
  // Restores go through /cart/add.js directly rather than addToCart, so nothing
  // was reserving one and a saved bag walked straight past the order cap.
  //
  // The slot is taken AFTER the add here, the opposite of addToCart. A saved line
  // carries no trustworthy "this is a gift card" marker of its own, and gift cards
  // must never take a slot, so the only reliable answer is the line Shopify hands
  // back. If capacity ran out in the meantime the set is pulled straight back out,
  // leaving the bag as it was rather than holding something that cannot be made.
  function needsOrderSlot(line) {
    const p = line.properties || {};
    if (p['_hidden'] === 'true') return false;
    if (p['_Item Type'] === 'kit') return false;
    return !line.gift_card;
  }

  // Returns true only when the line is genuinely in the bag afterwards.
  async function restoreOneItem(item) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: item.variantId, quantity: item.quantity, properties: item.properties || {} }] })
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      console.warn('[bag] saved line rejected, left in the saved bag:', cartErrorMessage(err));
      return false;
    }
    // Capacity used to be re-checked here and the set pulled straight back out
    // when the shop was full. That is gone on purpose (2026-10-12): being full
    // no longer stops anyone HOLDING a bag, it stops them checking out. Ripping
    // a saved set out of someone's bag on a different device, silently, was the
    // worst version of the old behaviour — they had already chosen it, and the
    // bag is the one place that choice was recorded.
    return true;
  }

  // Putting a saved bag back is not the same as adding to one, and three things
  // here used to be wrong in ways that quietly destroyed the saved copy:
  //
  //  - /cart/add.js was fired and never checked. fetch() does not reject on a 422,
  //    so a rejected restore fell through to syncCartToServer(), which wrote the
  //    still-empty local bag over the saved one. A single failed restore deleted
  //    the saved bag permanently.
  //  - every line went up in one batch, and /cart/add.js is all or nothing, so one
  //    unbuyable line took the whole bag down with it.
  //  - restored sets never reserved a capacity slot.
  //
  // The rule now: only write back when the local bag genuinely matches what was
  // saved. Anything we could not put back stays saved for the next attempt.
  async function restoreCartFromServer() {
    const tn = window.TALISNAILZ || {};
    if (!tn.customerId || !tn.workerUrl) return;
    if (sessionStorage.getItem('tn_cart_synced')) return;
    // Claimed up front so two tabs opening together cannot both restore the same
    // bag. Handed back on any failure, or one transient error would mute the
    // retry for the rest of the session.
    sessionStorage.setItem('tn_cart_synced', '1');

    let restored = 0;
    let removed = 0;
    let failed = 0;
    try {
      const res = await fetch(tn.workerUrl + '/api/cart/get', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId: tn.customerId, ts: tn.authTs, sig: tn.authSig })
      });
      if (!res.ok) { sessionStorage.removeItem('tn_cart_synced'); return; }
      const data = await res.json();
      const remoteItems = (data && data.items) || [];
      // No early return on an empty list. The server answered, so empty is a real
      // answer and usually means the bag was emptied on another device — which is
      // exactly the case that has to reach this one.
      const cart = await (await fetch('/cart.js')).json();
      const localKeys = new Set(cart.items.map(i => i.variant_id + '::' + propsKey(i.properties)));
      const toAdd = remoteItems.filter(ri => !localKeys.has(ri.variantId + '::' + propsKey(ri.properties)));

      // Deletions have to travel too. This only ever added before, so a line
      // removed on one device stayed in the other device's cart cookie, and that
      // device's next change pushed the whole stale bag back up and resurrected
      // it. Confirmed on Tali's own bag: two gift cards cleared on the laptop came
      // back from her phone, one of them doubled to quantity 2 because the
      // expired-date repair had rewritten its properties and the two copies no
      // longer looked like the same line.
      //
      // Legacy hidden holds are never synced, so they are never candidates for
      // removal here. purgeLegacyHolds() clears them instead.
      const remoteKeys = new Set(remoteItems.map(ri => ri.variantId + '::' + propsKey(ri.properties)));
      const toRemove = justSignedIn ? [] : cart.items.filter(function (i) {
        const p = i.properties || {};
        if (p['_hidden'] === 'true') return false;
        return !remoteKeys.has(i.variant_id + '::' + propsKey(i.properties));
      });

      if (!toAdd.length && !toRemove.length) {
        // Nothing to reconcile. On a fresh sign-in the local bag may still hold
        // gift cards added while signed out, which the server has never seen.
        if (justSignedIn && cart.items.length) await syncCartToServer();
        return;
      }

      for (const item of toAdd) {
        try {
          if (await restoreOneItem(item)) restored++; else failed++;
        } catch (e) {
          failed++;
          console.warn('[bag] could not restore a saved line', e);
        }
      }

      for (const line of toRemove) {
        try {
          await changeLine(line.key, 0);
          removed++;
        } catch (e) {
          failed++;
          console.warn('[bag] could not drop a stale line', e);
        }
      }

      if (restored || removed) {
        await updateCartCount();
      }
      // Sync only on a clean sweep. A partial reconcile that synced would delete
      // exactly the lines it had just failed to put back.
      if ((restored || removed || justSignedIn) && !failed) await syncCartToServer();
      if (failed) sessionStorage.removeItem('tn_cart_synced');
    } catch (e) {
      sessionStorage.removeItem('tn_cart_synced');
      console.warn('[bag] restore failed', e);
    }
  }

  // Set by handleLogoutCartClear, read by the reconcile below. A shopper who is
  // signed out can still add gift cards, and syncCartToServer bails without a
  // customerId, so none of that ever reached the server. On the first load after
  // signing in, those lines are the shopper's own work from a minute ago and get
  // pushed up rather than reconciled away.
  let justSignedIn = false;

  async function handleLogoutCartClear() {
    const tn = window.TALISNAILZ || {};
    const lastCustomerId = localStorage.getItem('tn_last_customer_id');
    const currentCustomerId = tn.customerId ? String(tn.customerId) : null;

    justSignedIn = !!currentCustomerId && lastCustomerId !== currentCustomerId;

    if (lastCustomerId && !currentCustomerId) {
      sessionStorage.removeItem('tn_cart_synced');
      try { await fetch('/cart/clear.js', { method: 'POST' }); } catch(e) {}
    }

    if (currentCustomerId) {
      localStorage.setItem('tn_last_customer_id', currentCustomerId);
    } else {
      localStorage.removeItem('tn_last_customer_id');
    }
  }

  // ── Sticky Nav ────────────────────────────────────────────────────
  function initStickyNav() {
    const navbar = document.querySelector('.navbar');
    if (!navbar) return;

    const spacer = document.createElement('div');
    spacer.className = 'navbar-spacer';
    navbar.after(spacer);

    let navbarTop = 0;

    function measure() {
      navbar.classList.remove('navbar--fixed');
      spacer.style.display = 'none';
      navbarTop = navbar.getBoundingClientRect().top + window.scrollY;
      spacer.style.height = navbar.offsetHeight + 'px';
      onScroll();
    }

    function onScroll() {
      if (window.scrollY >= navbarTop) {
        navbar.classList.add('navbar--fixed');
        spacer.style.display = 'block';
      } else {
        navbar.classList.remove('navbar--fixed');
        spacer.style.display = 'none';
      }
    }

    navbar.addEventListener('focusin', () => {
      if (!navbar.classList.contains('navbar--fixed')) return;
      const y = window.scrollY;
      requestAnimationFrame(() => {
        if (window.scrollY !== y) window.scrollTo(0, y);
      });
    });

    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', measure);
  }

  // ── FAQ accordion (fallback if page-level JS not loaded) ─────────
  function initFAQ() {
    document.querySelectorAll('.faq__q').forEach(btn => {
      if (btn.dataset.bound) return;
      btn.dataset.bound = '1';
      btn.addEventListener('click', () => {
        const item = btn.closest('.faq__item');
        const isOpen = item.classList.contains('is-open');
        document.querySelectorAll('.faq__item.is-open').forEach(i => i.classList.remove('is-open'));
        if (!isOpen) item.classList.add('is-open');
      });
    });
  }

  // ── Unread message badge ──────────────────────────────────────────
  // Lives here rather than in tn-chat.js so the nav badge doesn't drag the
  // whole chat module onto every page. Cached for 60s so clicking around the
  // site doesn't fire a request per navigation.

  async function chatSession() {
    const tn = window.TALISNAILZ || {};
    if (!tn.workerUrl || !tn.customerId || !tn.authSig) return null;

    try {
      const cached = JSON.parse(sessionStorage.getItem('tn_chat_sess') || 'null');
      if (cached && cached.token && cached.expiresAt * 1000 > Date.now() + 60000) return cached.token;
    } catch (e) {}

    try {
      const res = await fetch(tn.workerUrl + '/api/chat/session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId: tn.customerId, ts: tn.authTs, sig: tn.authSig })
      });
      const data = await res.json();
      if (!data.ok) return null;
      sessionStorage.setItem('tn_chat_sess', JSON.stringify({
        token: data.sessionToken, expiresAt: data.expiresAt
      }));
      return data.sessionToken;
    } catch (e) { return null; }
  }

  function paintUnread(count) {
    const badge = document.getElementById('tn-unread-count');
    if (badge) { badge.textContent = count; badge.hidden = count < 1; }
  }

  // Called by tn-chat.js the moment the Worker accepts a customer's read pointer.
  //
  // Writes a 0 into the cache rather than deleting the key. Deleting it would make
  // the next page load inside the 60s window refetch the summary; writing 0 means
  // it paints 0 straight from cache, and once the cache expires the summary call
  // returns 0 anyway because the server has now actually been told.
  function clearUnreadBadge() {
    try {
      sessionStorage.setItem('tn_unread', JSON.stringify({ n: 0, at: Date.now() }));
    } catch (e) {}
    paintUnread(0);
  }

  async function initUnreadBadge() {
    const tn = window.TALISNAILZ || {};
    if (!tn.customerId) return;
    // Gate on the count badge itself — this used to check for the nav dot,
    // which no longer exists, and would have silently disabled the badge.
    if (!document.getElementById('tn-unread-count')) return;

    try {
      const cached = JSON.parse(sessionStorage.getItem('tn_unread') || 'null');
      if (cached && Date.now() - cached.at < 60000) {
        paintUnread(cached.n);
        return;
      }
    } catch (e) {}

    const token = await chatSession();
    if (!token) return;

    try {
      const res = await fetch(tn.workerUrl + '/api/chat/summary', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionToken: token })
      });
      const data = await res.json();
      if (!data.ok) return;
      paintUnread(data.unread || 0);
      sessionStorage.setItem('tn_unread', JSON.stringify({ n: data.unread || 0, at: Date.now() }));
    } catch (e) {}
  }

  // ── Link prefetching ──────────────────────────────────────────────
  // Fetch a page on hover intent so the click lands on a warm cache. Pairs
  // with the CSS view transitions: those make navigation look smooth, this
  // makes it actually fast.
  //
  // The exclusion list is the important part. rel="prefetch" issues a real GET,
  // so prefetching a logout or cart-clear URL would EXECUTE it — silently
  // signing the customer out just because they hovered a menu.

  const PREFETCH_DENY = [
    '/account/logout',
    '/customer_authentication/logout',
    '/cart/clear',
    '/cart/change',
    '/cart/add',
    '/checkout',
    // staff-only; no reason to warm either for anyone. Both paths are listed
    // because /pages/studio stays live as an alias until the page is renamed
    // in the Shopify admin.
    '/pages/headquarters',
    '/pages/studio'
  ];

  function initPrefetch() {
    // Respect data saver and genuinely slow links — prefetching there costs
    // the user money or bandwidth they can't spare.
    const conn = navigator.connection;
    if (conn && (conn.saveData || /2g/.test(conn.effectiveType || ''))) return;
    if (!document.createElement('link').relList?.supports?.('prefetch')) return;

    const done = new Set([location.pathname]);
    let budget = 15; // hard cap, so a mouse sweep down the nav can't spiral
    let timer = null;

    function eligible(a) {
      if (!a || !a.href) return false;
      if (a.target && a.target !== '_self') return false;
      if (a.hasAttribute('download') || a.hasAttribute('data-no-prefetch')) return false;
      let url;
      try { url = new URL(a.href); } catch (e) { return false; }
      if (url.origin !== location.origin) return false;
      if (url.pathname === location.pathname) return false;
      if (PREFETCH_DENY.some(p => url.pathname.startsWith(p))) return false;
      if (done.has(url.pathname)) return false;
      return true;
    }

    function prefetch(a) {
      if (budget <= 0 || !eligible(a)) return;
      const url = new URL(a.href);
      done.add(url.pathname);
      budget--;
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = url.pathname + url.search;
      document.head.appendChild(link);
    }

    // 65ms of dwell separates "moving toward this link" from "swept past it".
    document.addEventListener('pointerover', (e) => {
      const a = e.target.closest?.('a');
      if (!a) return;
      clearTimeout(timer);
      timer = setTimeout(() => prefetch(a), 65);
    }, { passive: true });

    document.addEventListener('pointerout', () => clearTimeout(timer), { passive: true });

    // Touch has no hover, but touchstart fires ~100ms before the click.
    document.addEventListener('touchstart', (e) => {
      const a = e.target.closest?.('a');
      if (a) prefetch(a);
    }, { passive: true });
  }

  // ── Init ──────────────────────────────────────────────────────────
  // ── profile-started ───────────────────────────────────────────────
  //
  // Someone signed in and has no profile tag at all yet. Mark them so they
  // appear in headquarters and can be helped before they drift off. Runs from
  // any page because sign-in can happen from the cart or a product page, not
  // only from the nail profile page.
  //
  // The tag grants nothing — every membership gate still asks for
  // profile-pending/confirmed/staff. The Worker re-checks the account's real
  // tags before writing, so a stale tab cannot mis-tag a paying customer.
  function initProfileStarted() {
    const tn = window.TALISNAILZ || {};
    if (!tn.customerId || !tn.workerUrl || !tn.authSig) return;
    // Already further along, or already marked — nothing to do.
    if (tn.profileStatus === 'pending' || tn.profileStatus === 'confirmed') return;
    if (tn.profileStarted) return;

    // The tag write is async, so the next few pageviews would still render
    // profileStarted:false and fire again. One attempt per tab settles it.
    const flag = 'tn_started_tagged:' + tn.customerId;
    try {
      if (sessionStorage.getItem(flag)) return;
      sessionStorage.setItem(flag, '1');
    } catch (e) {}

    fetch(tn.workerUrl + '/api/tag-started', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customerId: tn.customerId, ts: tn.authTs, sig: tn.authSig })
    }).catch(() => {});
  }

  document.addEventListener('DOMContentLoaded', async () => {
    initFAQ();
    initProfileStarted();
    await handleLogoutCartClear();
    initStickyNav();
    // Before the restore, so a bag that still carries an old hidden hold is
    // cleaned before anything counts what is in it.
    await purgeLegacyHolds();
    restoreCartFromServer();
    initUnreadBadge();
    initSoldOutBanner();
    initPrefetch();
  });

  // Expose globally for use in section JS
  // ── Editing a bag line ────────────────────────────────────────────
  // The bag links back here as ?edit=<line key>. Each surface (PDP, gift card,
  // builder) restores its own fields from the returned line, but they all find
  // it the same way. Returns null when there is no edit param or the line has
  // since left the bag — a stale link then behaves like a normal visit rather
  // than half-restoring something that no longer exists.
  async function getEditLine() {
    const key = new URLSearchParams(location.search).get('edit');
    if (!key) return null;
    try {
      const cart = await (await fetch('/cart.js')).json();
      return cart.items.find(i => i.key === key) || null;
    } catch (e) { return null; }
  }

  // needsOrderSlot is exported for the bag page: removing the last nail set from
  // a bag that also holds a gift card has to unblock checkout without a reload,
  // so the "is anything in here made-to-order" question has to be answerable
  // client-side, not only from Liquid at render time.
  // ── Full-capacity pop-up ──────────────────────────────────────────
  //
  // Fires AFTER a made-to-order item lands in the bag, never instead of it.
  // Being at capacity does not refuse an add, so without this the first a
  // shopper hears of a closed shop is the dead checkout button on the bag page.
  // This says it at the moment of intent instead.
  //
  // Deliberately silent in three cases:
  //   - capacity unknown. Every other capacity path falls OPEN and allows the
  //     sale; announcing a closed shop on a Worker hiccup would be the one
  //     place that failed closed, and it would be wrong.
  //   - slots still left. "2 open, your bag needs 3" is the bag page's job.
  //     Two surfaces disagreeing about one situation is worse than one.
  //   - already shown this visit. Four sets must not mean four pop-ups.
  const CAPACITY_POPUP_KEY = 'tn_capacity_popup_shown';

  function capacityPopupSeen() {
    try { return sessionStorage.getItem(CAPACITY_POPUP_KEY) === '1'; } catch (e) { return false; }
  }

  async function maybeCapacityPopup() {
    const el = document.getElementById('tn-capacity-popup');
    if (!el || capacityPopupSeen()) return;

    const remaining = await capacityRemaining();
    if (remaining === null || remaining > 0) return;

    try { sessionStorage.setItem(CAPACITY_POPUP_KEY, '1'); } catch (e) {}

    // Resolves on dismiss. Callers that navigate afterwards await it, or the
    // next page would wipe the pop-up before it was read.
    return new Promise(function (resolve) {
      const card = el.querySelector('.tn-capacity-popup__card');
      const okBtn = el.querySelector('.tn-capacity-popup__ok');
      const closeBtn = el.querySelector('.tn-capacity-popup__close');
      const lastFocus = document.activeElement;

      function onKey(e) { if (e.key === 'Escape') close(); }
      function onBackdrop(e) { if (card && !card.contains(e.target)) close(); }
      function close() {
        el.classList.remove('is-open');
        document.removeEventListener('keydown', onKey);
        el.removeEventListener('click', onBackdrop);
        if (okBtn) okBtn.removeEventListener('click', close);
        if (closeBtn) closeBtn.removeEventListener('click', close);
        try { if (lastFocus && lastFocus.focus) lastFocus.focus(); } catch (e) {}
        resolve();
      }

      el.classList.add('is-open');
      document.addEventListener('keydown', onKey);
      el.addEventListener('click', onBackdrop);
      if (okBtn) okBtn.addEventListener('click', close);
      if (closeBtn) closeBtn.addEventListener('click', close);
      try { (okBtn || closeBtn || card).focus(); } catch (e) {}
    });
  }

  window.TN = { showToast, addToCart, removeFromCart, updateCartCount, syncCartToServer, getEditLine, capacityRemaining, clearUnreadBadge, needsOrderSlot, maybeCapacityPopup };
})();